import { useMemo } from 'react';

import { createStateAggregatorContext, useCreateStateAggregator } from '#utils/state-aggregator-context.js';
import { generateCommandBarProvidersList, getProviderRelevance } from './command-bar-utils.js';
import { CommandBarGroupProvider } from './components/command-bar-group-provider.js';

import type { ReactNode } from 'react';
import type { CommandBarAggregatedState, CommandBarProviderConfig } from './command-bar-types.js';

// Create the state aggregator context for command bar
export const {
	context: CommandBarAggregatorContext,
	AddStateValue: AddCommandBarStateValue,
	useAddStateValue: useAddCommandBarStateValue,
	useStateAggregatorAllValues: useCommandBarAllValues,
	useStateAggregatorLastValue: useCommandBarLastValue,
} = createStateAggregatorContext<CommandBarAggregatedState>();

/**
 * Hook to create the command bar state aggregator.
 * Used by CommandBarProvider.
 */
export const useCommandBarAggregator = () => useCreateStateAggregator<CommandBarAggregatedState>();

/**
 * Groups providers by their group property.
 * Exported for testing.
 */
export function groupProviders(providers: CommandBarProviderConfig<any>[]) {
	return Array.from(
		providers
			.reduce((acc, provider) => {
				const group = acc.get(provider.group ?? null) ?? [];
				group.push(provider);
				acc.set(provider.group ?? null, group);
				return acc;
			}, new Map<string | null, CommandBarProviderConfig<any>[]>())
			.entries()
			.flatMap<CommandBarProviderConfig<any>>(([group, providers]) => {
				if (group === null) {
					return providers;
				}

				const entry: CommandBarProviderConfig<any> = {
					id: `group:${group}`,
					getRelevance: (search: string) => {
						return providers.reduce((acc, p) => Math.max(acc, getProviderRelevance(search, p)), 0);
					},
					component: CommandBarGroupProvider,
					props: {
						heading: group,
						providers,
					},
					searchPrefix: providers.flatMap((p) => p.searchPrefix ?? []),
				};

				return [entry];
			}),
	);
}

/**
 * Hook to get all registered providers, sorted by relevance to the current search.
 * Providers with higher relevance scores are rendered first.
 *
 * @param search - The current search query
 * @returns Array of providers sorted by relevance (highest first)
 */
export function useCommandBarProviders(search: string): CommandBarProviderConfig<any>[] {
	const providers = useCommandBarAllValues('providers');

	// First apply grouping
	const groupedProviders = useMemo(() => groupProviders(providers), [providers]);

	// Then filter providers by the search string
	return useMemo(() => {
		return generateCommandBarProvidersList(search, groupedProviders);
	}, [search, groupedProviders]);
}

/**
 * Hook to get the current placeholder text.
 * Falls back to default if none registered.
 */
export function useCommandBarPlaceholder(): string {
	const placeholder = useCommandBarLastValue('placeholder');
	return placeholder ?? 'Type a command or search...';
}

/**
 * Hook to get all registered empty state message elements.
 * Each element should self-filter (by returning `null`) when its relevant search prefix isn't active.
 * Falls back to a default "No results found." message if none are registered.
 */
export function useCommandBarEmptyMessages(): ReactNode[] {
	const messages = useCommandBarAllValues('emptyStateMessage').filter((m) => m !== null);
	return useMemo(() => (messages.length > 0 ? messages : ['No results found.']), [messages]);
}
